import { _js } from '@ifixit/utils/localize';
import {
   DropdownMenuContent,
   DropdownMenuItem,
   DropdownMenuLabel,
   DropdownMenuSeparator,
} from '@ifixit/ui/components/dropdown-menu';
import type { useDragReorder } from 'Shared/editor/use_drag_reorder';
import type { ApiStep } from './types';

/** The step rail's drag hook, shared by the list rows and the grid tiles. */
export type RailDrag = ReturnType<typeof useDragReorder>;

/** What the list row and the grid tile of one saved step both take. */
export type RailStepProps = {
   step: ApiStep;
   displayIndex: number;
   isSelected: boolean;
   /** Dragged to a position the server doesn't have yet. */
   isMoved: boolean;
   isDragging: boolean;
   /** The guide has a video, so a step's visual is its video segment and a
       missing step image isn't a gap. */
   hasVideo: boolean;
   rowProps: RailDrag['rowProps'];
   handleProps: RailDrag['handleProps'];
   onSelect: (stepid: number) => void;
   /** Open the new-step editor inserting next to this step. */
   onInsert: (stepid: number, where: 'above' | 'below') => void;
   /** Open the new-step editor prefilled with this step's content. */
   onDuplicate: (stepid: number) => void;
   /** Step-history URL prefix; null hides the history item. */
   historyUrlBase: string | null;
};

export function stepThumb(step: ApiStep): string | null {
   if (step.media.type !== 'image' || step.media.data.length === 0) {
      return null;
   }
   const image = step.media.data[0];
   return image.thumbnail ?? image.standard ?? image.original ?? null;
}

/**
 * A publish-readiness nudge (not an error): no image, or a blank line.
 * Empty when the step is ready.
 */
export function stepAttention(step: ApiStep, hasVideo: boolean): string {
   return [
      !hasVideo && step.media.type === 'image' && step.media.data.length === 0
         ? _js('No image yet')
         : null,
      step.lines.some(line => line.text_raw.trim().length === 0) ? _js('Has an empty line') : null,
   ]
      .filter(reason => reason !== null)
      .reduce((list, reason) => (list === '' ? reason : _js('%1, %2', list, reason)), '');
}

/** The per-step actions both layouts open from their ⋮ button. */
export function StepMenuContent({
   step,
   attention,
   onInsert,
   onDuplicate,
   historyUrlBase,
}: Pick<RailStepProps, 'step' | 'onInsert' | 'onDuplicate' | 'historyUrlBase'> & {
   attention: string;
}) {
   return (
      <DropdownMenuContent align="end">
         {/* The tooltip needs hover or focus. Touch has neither, and the list row's trigger is a
             non-focusable span, so keyboard has neither there. The menu repeats the reason. */}
         {attention !== '' && (
            <>
               <DropdownMenuLabel className="text-xs font-normal text-amber-700">
                  {attention}
               </DropdownMenuLabel>
               <DropdownMenuSeparator />
            </>
         )}
         <DropdownMenuItem onSelect={() => onInsert(step.stepid, 'above')}>
            {_js('Insert step above')}
         </DropdownMenuItem>
         <DropdownMenuItem onSelect={() => onInsert(step.stepid, 'below')}>
            {_js('Insert step below')}
         </DropdownMenuItem>
         <DropdownMenuItem onSelect={() => onDuplicate(step.stepid)}>
            {_js('Duplicate step')}
         </DropdownMenuItem>
         {historyUrlBase !== null && (
            <DropdownMenuItem asChild>
               {/* Slot puts the item styles on the <a> but no text color, so the
                   document's `a` rule would paint it blue among black items. */}
               <a href={historyUrlBase + step.stepid} className="text-popover-foreground">
                  {_js('Step history')}
               </a>
            </DropdownMenuItem>
         )}
      </DropdownMenuContent>
   );
}
